import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowRight, 
  CheckCircle, 
  Globe, 
  Palette, 
  TrendingUp,
  Users,
  Award,
  Briefcase,
  ChevronRight,
  Mail
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to use CSS variable with fallback
const cssVar = (name, fallback) => `var(--color-${name}, ${fallback})`;

const PublicHomePage = () => {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [content, setContent] = useState(null);

  // Default content - fallback if nothing in database
  const defaults = {
    badge_text: '40+ Years of Marketing Excellence',
    hero_title: 'Marketing That Moves the Needle',
    hero_subtitle: 'Enterprise-level marketing expertise with a personal, collaborative approach. We help businesses sharpen their message, strengthen their brand, and execute marketing that delivers real results.',
    hero_cta_text: 'Get a Free Consultation',
    stats: [
      { value: '40+', label: 'Years Experience' },
      { value: '24+', label: 'Years at VELUX' },
      { value: '$M+', label: 'Budgets Managed' },
      { value: '12+', label: 'Team Members Led' }
    ],
    cta_title: 'Ready to Elevate Your Marketing?',
    cta_subtitle: "Let's discuss how we can help you sharpen your message, strengthen your brand, and achieve measurable results.",
    cta_button_text: 'Get a Free Consultation'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, portfolioRes, contentRes] = await Promise.all([
          axios.get(`${API}/public/services`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/portfolio?featured_only=true`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/site-content/homepage`).catch(() => ({ data: {} }))
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setPortfolio(portfolioRes.data.slice(0, 3));
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setContent(contentRes.data);
        }
      } catch (err) {
        console.log('Error fetching data');
      }
    };
    fetchData();
  }, []);

  // Merge CMS content with defaults
  const c = { ...defaults, ...content };
  const stats = (content?.stats && content.stats.length > 0) ? content.stats : defaults.stats;

  const defaultServices = [
    {
      title: 'Web Design & Development',
      short_description: 'Custom websites and web applications built to convert visitors into customers.',
      icon: 'globe'
    },
    {
      title: 'Brand Strategy',
      short_description: 'Strategic brand positioning that sets you apart from the competition.',
      icon: 'palette'
    },
    {
      title: 'Digital Marketing',
      short_description: 'Full-funnel marketing programs that drive measurable results.',
      icon: 'trending-up'
    },
    {
      title: 'Content Marketing',
      short_description: 'Compelling content that builds authority and engages your audience.',
      icon: 'users'
    }
  ];

  const displayServices = services.length > 0 ? services : defaultServices;

  const getIcon = (iconName) => {
    const icons = {
      'globe': Globe,
      'palette': Palette,
      'trending-up': TrendingUp,
      'users': Users
    };
    const Icon = icons[iconName] || Globe;
    return <Icon className="w-8 h-8" />;
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section 
        className="pt-40 pb-20 lg:pt-48 lg:pb-32"
        style={{ background: `linear-gradient(to bottom right, var(--color-hero-bg-from, #F8FAFC), var(--color-hero-bg-to, #FFFFFF))` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
              >
                <Award className="w-4 h-4" />
                {c.badge_text}
              </div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
                style={{ color: 'var(--color-heading-text, #0F172A)' }}
              >
                {c.hero_title.includes('Moves') ? (
                  <>Marketing That<span style={{ color: 'var(--color-primary, #1F3C4F)' }}> Moves the Needle</span></>
                ) : (
                  c.hero_title
                )}
              </h1>
              <p 
                className="text-xl leading-relaxed mb-8 max-w-xl"
                style={{ color: 'var(--color-body-text, #475569)' }}
              >
                {c.hero_subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="text-lg px-8 py-6 btn-primary-hover"
                    style={{ 
                      backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))', 
                      color: 'var(--color-button-text, #FFFFFF)',
                      borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                    }}
                  >
                    {c.hero_cta_text}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="text-lg px-8 py-6 btn-secondary-hover"
                    style={{ 
                      borderColor: 'var(--color-primary, #1F3C4F)',
                      color: 'var(--color-primary, #1F3C4F)',
                      backgroundColor: 'transparent',
                      borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                    }}
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div 
                className="absolute inset-0 rounded-3xl transform rotate-3"
                style={{ background: `linear-gradient(to bottom right, var(--color-primary, #1F3C4F)33, transparent)` }}
              ></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8" style={{ boxShadow: 'var(--card-shadow)' }}>
                <div className="grid grid-cols-2 gap-6">
                  {stats.slice(0, 4).map((stat, idx) => (
                    <div 
                      key={idx} 
                      className="text-center p-6 rounded-2xl"
                      style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
                    >
                      <div 
                        className="text-4xl font-bold mb-2"
                        style={{ color: 'var(--color-primary, #1F3C4F)' }}
                      >{stat.value}</div>
                      <div 
                        className="text-sm"
                        style={{ color: 'var(--color-body-text, #475569)' }}
                      >{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12" style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            <div>
              <CheckCircle className="w-8 h-8 mx-auto mb-3 opacity-80" />
              <div className="font-semibold">Brand Strategy</div>
            </div>
            <div>
              <CheckCircle className="w-8 h-8 mx-auto mb-3 opacity-80" />
              <div className="font-semibold">Web Development</div>
            </div>
            <div>
              <CheckCircle className="w-8 h-8 mx-auto mb-3 opacity-80" />
              <div className="font-semibold">Digital Marketing</div>
            </div>
            <div>
              <CheckCircle className="w-8 h-8 mx-auto mb-3 opacity-80" />
              <div className="font-semibold">Lead Generation</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Services That Drive Results
            </h2>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              Strategic marketing solutions backed by decades of experience with industry-leading brands.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {displayServices.map((service, idx) => (
              <Card 
                key={idx} 
                className="border-0 shadow-lg hover:shadow-xl transition-shadow group"
                style={{ 
                  backgroundColor: 'var(--color-card-bg, #FFFFFF)',
                  boxShadow: 'var(--card-shadow)',
                  borderRadius: `calc(var(--layout-card-border-radius, 1) * 1rem)`
                }}
              >
                <CardContent className="p-8">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:text-white transition-colors"
                    style={{ 
                      backgroundColor: 'var(--color-primary, #1F3C4F)1a',
                      color: 'var(--color-primary, #1F3C4F)'
                    }}
                  >
                    {getIcon(service.icon)}
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{ color: 'var(--color-heading-text, #0F172A)' }}
                  >{service.title}</h3>
                  <p 
                    className="mb-4"
                    style={{ color: 'var(--color-body-text, #475569)' }}
                  >{service.short_description}</p>
                  <Link 
                    to="/services" 
                    className="inline-flex items-center font-medium hover:gap-2 transition-all"
                    style={{ color: 'var(--color-primary, #1F3C4F)' }}
                  >
                    Learn More <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline" 
                size="lg"
                style={{ 
                  borderColor: 'var(--color-primary, #1F3C4F)',
                  color: 'var(--color-primary, #1F3C4F)',
                  borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                }}
                className="hover:text-white"
              >
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section 
        className="py-20 lg:py-32"
        style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ color: 'var(--color-heading-text, #0F172A)' }}
              >
                Enterprise Experience, Personal Approach
              </h2>
              <p 
                className="text-lg mb-6 leading-relaxed"
                style={{ color: 'var(--color-body-text, #475569)' }}
              >
                After retiring as the Senior Director of Marketing Communications for VELUX North America, 
                Channing Hoyle launched SPT Digital Marketing to help businesses elevate their marketing 
                with the same strategic discipline, creative excellence, and operational precision he 
                brought to some of the most recognized brands in the home improvement industry.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-primary, #1F3C4F)' }} />
                  <span style={{ color: 'var(--color-body-text, #475569)' }}>Led large in-house creative teams and managed multi-million dollar budgets</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-primary, #1F3C4F)' }} />
                  <span style={{ color: 'var(--color-body-text, #475569)' }}>Built integrated marketing programs that drove category growth</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-primary, #1F3C4F)' }} />
                  <span style={{ color: 'var(--color-body-text, #475569)' }}>Expertise in digital marketing, advertising, and lead generation</span>
                </li>
              </ul>
              <Link to="/about">
                <Button 
                  size="lg"
                  className="btn-primary-hover"
                  style={{ 
                    backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                    color: 'var(--color-button-text, #FFFFFF)',
                    borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                  }}
                >
                  Learn More About Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div 
                className="rounded-3xl shadow-xl p-8"
                style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)', boxShadow: 'var(--card-shadow)' }}
              >
                <div 
                  className="border-l-4 pl-6"
                  style={{ borderColor: 'var(--color-primary, #1F3C4F)' }}
                >
                  <blockquote 
                    className="text-xl italic mb-4"
                    style={{ color: 'var(--color-body-text, #475569)' }}
                  >
                    "At SPT Digital Marketing, we blend enterprise-level marketing experience 
                    with a personal, collaborative approach — helping clients sharpen their message, 
                    strengthen their brand, and execute marketing that actually moves the needle."
                  </blockquote>
                  <div className="font-semibold" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Channing Hoyle</div>
                  <div style={{ color: 'var(--color-muted-text, #94A3B8)' }}>Founder, SPT Digital Marketing</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 lg:py-32"
        style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {c.cta_title}
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            {c.cta_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6"
                style={{ 
                  backgroundColor: 'var(--color-button-secondary-bg, #FFFFFF)',
                  color: 'var(--color-primary, #1F3C4F)',
                  borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                {c.cta_button_text || 'Get a Free Consultation'}
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6 border-white text-white hover:bg-white/10"
                style={{ borderRadius: 'var(--btn-computed-radius, 0.5rem)' }}
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PublicHomePage;
